/**
 * One tag, in the shape the app already reads as a tag.
 *
 * It started under the module card's facts row and moved here the day a second
 * surface needed the same thing: a classroom card that drew its own chips would be
 * the second chip row this app has had to keep in step, and the first pair had
 * already drifted in padding and in wording before anybody noticed (see the note in
 * `ModuleFactsRow`). One component, one padding, one tone.
 *
 * The tones are the app's own, not decoration:
 *
 *  - **plain** — `contrast_50`, for a fact that labels the thing.
 *  - **accent** — `contrast_100`, deeper, for the one fact that says *what kind of
 *    thing this is*.
 *  - **reward** — the same depth, in the app's blue, for the one fact a reader
 *    presses for (see `ModuleFactsTags`).
 *
 * An `icon` is optional and sits before the words: the icons in a chip are the
 * fastest way to scan a row of them, and a caller that has one to give should.
 */
import {View} from 'react-native'

import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'

export function Chip({
  label,
  icon,
  accent,
  reward,
}: {
  label: React.ReactNode
  /** Drawn before the label, sized by the caller. */
  icon?: React.ReactNode
  /** The one chip that says what kind of thing this is. */
  accent?: boolean
  /**
   * The chip a reader plays for.
   *
   * The coins are the one fact on a module's card that is about *winning* rather
   * than about the course, and the card keeps one colour for that: the app's blue,
   * on the deepest chip in the row, so it reads as a prize rather than as another
   * label.
   */
  reward?: boolean
}) {
  const t = useTheme()
  return (
    <View
      style={[
        styles.chip,
        a.flex_row,
        a.align_center,
        a.gap_xs,
        accent || reward ? t.atoms.bg_contrast_100 : t.atoms.bg_contrast_50,
      ]}>
      {icon}
      <Text
        emoji
        numberOfLines={1}
        style={[
          a.text_xs,
          a.font_semi_bold,
          t.atoms.text,
          reward ? {color: t.palette.primary_500} : null,
        ]}>
        {label}
      </Text>
    </View>
  )
}

const styles = {
  chip: {
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderRadius: 999,
  },
} as const
